import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// Import do Módulo de Mercado (desenvolvimento isolado)
import MarketModule from "./components/MarketModule";
import BalcaoPublicoPage from "./components/BalcaoPublicoPage";
import CursosPublicPage from "./components/CursosPublicPage";
import ParceirosPage from "./pages/ParceirosPage";
import TutoriaisPage from "./pages/TutoriaisPage";

// Error Boundary global: erros de Portal são auto-recuperados; qualquer outro erro
// mostra tela de recuperação amigável (em vez de tela branca no celular)
class PortalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isPortalError: false };
  }

  static getDerivedStateFromError(error) {
    const isPortal = !!(error?.message && error.message.includes('removeChild'));
    return { hasError: true, isPortalError: isPortal };
  }

  componentDidCatch(error, errorInfo) {
    if (error?.message && error.message.includes('removeChild')) {
      console.debug('Portal DOM sync issue handled gracefully');
    } else {
      console.error('Erro não tratado na aplicação:', error, errorInfo);
    }
  }

  componentDidUpdate() {
    // Auto-recupera erros benignos de Portal fora do ciclo de render
    if (this.state.hasError && this.state.isPortalError) {
      this._resetTimer = setTimeout(
        () => this.setState({ hasError: false, isPortalError: false }),
        100
      );
    }
  }

  componentWillUnmount() {
    if (this._resetTimer) clearTimeout(this._resetTimer);
  }

  handleReload = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      if (window.caches?.keys) {
        window.caches.keys().then((keys) => keys.forEach((k) => window.caches.delete(k)));
      }
    } catch (e) { /* storage indisponível */ }
    window.location.replace('/');
  };

  render() {
    if (this.state.hasError && this.state.isPortalError) {
      // Enquanto o auto-reset (componentDidUpdate) não dispara, segue renderizando o app
      return this.props.children;
    }

    if (this.state.hasError) {
      return (
        <div data-testid="app-error-recovery" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', textAlign: 'center', fontFamily: 'sans-serif', background: '#f0fdf4' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🌱</div>
          <h1 style={{ fontSize: '20px', color: '#166534', marginBottom: '8px' }}>Ops, algo deu errado</h1>
          <p style={{ fontSize: '14px', color: '#4b5563', marginBottom: '24px', maxWidth: '320px' }}>
            Encontramos um problema ao carregar o HeveaTalent. Toque no botão abaixo para limpar os dados antigos e recarregar.
          </p>
          <button
            data-testid="app-error-reload-btn"
            onClick={this.handleReload}
            style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px 32px', fontSize: '16px', fontWeight: 600, cursor: 'pointer' }}
          >
            🔄 Recarregar aplicativo
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Users, Briefcase, MapPin, Clock, Star, Award, TrendingUp, ShoppingCart, Package, Home, Wrench, Truck, Zap, MoreHorizontal, Globe, Calendar, Plus, Trash2 } from "lucide-react";
import { TranslationProvider, useTranslation } from "./translations";
import { TodaysHabits } from "./components/TodaysHabits";
import OnboardingChecklist from "./components/OnboardingChecklist";

// Evaluation System Pages
import WorkerDashboard from "./pages/WorkerDashboard";
import EvaluationForm from "./pages/EvaluationForm";
import EvaluationsPage from "./pages/EvaluationsPage";
import TechnicalReferences from "./pages/TechnicalReferences";
import CertificationOptIn from "./pages/CertificationOptIn";
import PublicRecommendPage from "./pages/PublicRecommendPage";

// Contextual Performance - Phase 2
import ContextualPerformanceDashboard from "./pages/ContextualPerformanceDashboard";
import EvolutionChart from "./components/EvolutionChart";

// Phase 4 - Reports Dashboard for Employers
import ReportsDashboard from "./pages/ReportsDashboard";
import LanguageSelector from "./components/LanguageSelector";
import { AchievementBadge, ProfessionalAchievements } from "./components/AchievementBadge";
// ProfessionalRating foi removido - sistema de estrelas eliminado em favor do Tripé (Justiça)
import NotificationBell from "./components/NotificationBell";
import { LoginDialog, RegisterDialog } from "./components/AuthDialogs";
import ConfirmarAnuncioPage from "./pages/ConfirmarAnuncioPage";
import PublicOfferPage from "./pages/PublicOfferPage";
import CursoLandingPage from "./pages/CursoLandingPage";
import LandingPage from "./components/LandingPage";
import PropositoPage from "./pages/PropositoPage";
import ConviteTour from "./pages/ConviteTour";
import CurrentPlanBanner from "./components/CurrentPlanBanner";
import { FeatureConfirmDialog, PixPaymentDialog } from "./components/MarketplaceDialogs";
import { track } from "./lib/tracker";
import ImageManager from "./components/ImageManager";
import JobCard from "./components/JobCard";
import WorkOfferCard from "./components/WorkOfferCard";
import EditProductDialog from "./components/EditProductDialog";
import RateSellerButton from "./components/RateSellerButton";
import PropertyFormDialog from "./components/PropertyFormDialog";
import ExampleModal from "./components/ExampleModal";
import JobsList from "./components/JobsList";
import MonthlyChartsView from "./components/MonthlyChartsView";
import MarketplaceItemCard from "./components/MarketplaceItemCard";
import MarketplaceForm from "./components/MarketplaceForm";
import JobPostForm from "./components/JobPostForm";
import WorkOfferForm from "./components/WorkOfferForm";
import MarketplaceBrowser from "./components/MarketplaceBrowser";
import MarketplaceDashboard from "./components/MarketplaceDashboard";
import EmployerDashboard from "./components/EmployerDashboard";
import ProfessionalDashboard from "./components/ProfessionalDashboard";
import CommunityTipsView from "./components/CommunityTipsView";
import SubscriptionPlansView from "./components/SubscriptionPlansView";
import MySubscriptionView from "./components/MySubscriptionView";
import PaymentSuccess from "./pages/PaymentSuccess";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PaymentCancel from "./pages/PaymentCancel";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import UpgradeGate, { TrialBanner } from "./components/UpgradeGate";
import { useSubscription } from "./hooks/useSubscription";
import ContactMessagesPanel from "./components/ContactMessagesPanel";
import AdminStatsPanel from "./components/AdminStatsPanel";
import AdminUsersPanel from "./components/AdminUsersPanel";
import AdminDataCleanupPanel from "./components/AdminDataCleanupPanel";
import RadarAdminPanel from "./components/RadarAdminPanel";
import { FinanceiroPanel } from "./components/FinanceiroPanel";
import JobBroadcastPanel from "./components/JobBroadcastPanel";
import CursosAdminPanel from "./components/CursosAdminPanel";
import DiagnosticoIAPanel from "./components/DiagnosticoIAPanel";
import AdminFeedbackPanel from "./components/AdminFeedbackPanel";
import ExperienceFeedbackCard from "./components/ExperienceFeedbackCard";
import FeedbackPublicPage from "./components/FeedbackPublicPage";
import { PriceWeatherBar } from "./components/PriceWeatherBar";

import ProfessionalsSearchView from "./components/ProfessionalsSearchView";
import PublicJobPage from "./pages/PublicJobPage";
import CityJobsPage from "./pages/CityJobsPage";
import CityIndexPage from "./pages/CityIndexPage";
import EvaluationsDashboard from "./components/EvaluationsDashboard";
import RecordFormDialog from "./components/RecordFormDialog";
import MonthlyCalendarView from "./components/MonthlyCalendarView";
import BleedingDailyTable from "./components/BleedingDailyTable";
import WorkersDatabase from "./components/WorkersDatabase";
import ChatSystem from "./components/ChatSystem";
import InstallPrompt from "./components/InstallPrompt";
import UpdateNotice from "./components/UpdateNotice";
import MonthlyReportView from "./components/MonthlyReportView";
import TeamReportView from "./components/TeamReportView";
import SangradorDashboard from "./components/SangradorDashboard";
import YearlySummaryCard from "./components/YearlySummaryCard";
import EthrelAlertBanner from "./components/EthrelAlertBanner";
import CalendarGuideModal from "./components/CalendarGuideModal";
import PropertyPermissionsGuide, { PermissionsHelpTooltip } from "./components/PropertyPermissionsGuide";
import { ReferralCard } from "./components/ReferralCard";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Authentication context
const AuthContext = React.createContext();

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/me`);
      setUser(response.data);
    } catch (error) {
      logout();
    }
  };

  const login = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
    localStorage.setItem('token', userToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${userToken}`;
    try {
      const pending = JSON.parse(localStorage.getItem('pending_vaga') || 'null');
      if (pending?.id && Date.now() - (pending.ts || 0) < 2 * 3600 * 1000) {
        localStorage.removeItem('pending_vaga');
        window.location.assign(`/vaga/${pending.id}`);
        return;
      }
      localStorage.removeItem('pending_vaga');
      const pendingOferta = JSON.parse(localStorage.getItem('pending_oferta') || 'null');
      if (pendingOferta?.id && Date.now() - (pendingOferta.ts || 0) < 2 * 3600 * 1000) {
        localStorage.removeItem('pending_oferta');
        window.location.assign(`/oferta/${pendingOferta.id}`);
        return;
      }
      localStorage.removeItem('pending_oferta');
    } catch { /* ignora pending_vaga corrompido */ }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated: !!user, refreshUser: fetchCurrentUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => React.useContext(AuthContext);

// Language Selector Component
// LanguageSelector foi extraído para /components/LanguageSelector.jsx

// Seal level component
// AchievementBadge e ProfessionalAchievements foram extraídos para /components/AchievementBadge.jsx

// ProfessionalRating foi extraído para /components/ProfessionalRating.jsx

// LandingPage foi extraída para /components/LandingPage.jsx

// Dashboard for authenticated users
const Dashboard = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { canAccess, planName, isTrial, trialExpired, daysRemaining, alertLevel } = useSubscription();
  const [jobs, setJobs] = useState([]);
  const [workOffers, setWorkOffers] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [marketplaceItems, setMarketplaceItems] = useState([]);
  const [myMarketplaceItems, setMyMarketplaceItems] = useState([]);
  const [activeTab, setActiveTab] = useState('jobs');

  useEffect(() => { track(activeTab); }, [activeTab]);
  const [isNavigating, setIsNavigating] = useState(false);

  const tabContentRef = React.useRef(null);

  // Função para mudança segura de aba
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    // Scroll suave para o conteúdo da aba
    setTimeout(() => {
      if (tabContentRef.current) {
        tabContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Expor função de navegação globalmente para outros componentes
  useEffect(() => {
    window.navigateToTab = (tab) => {
      setActiveTab(tab);
    };
    return () => {
      delete window.navigateToTab;
    };
  }, []);

  // Listener para evento customizado de mudança de tab
  useEffect(() => {
    const handleChangeTab = (e) => {
      const newTab = e.detail;
      if (newTab) {
        setActiveTab(newTab);
      }
    };
    
    window.addEventListener('changeTab', handleChangeTab);
    return () => window.removeEventListener('changeTab', handleChangeTab);
  }, []); // Removido isNavigating para evitar recriação desnecessária

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await fetchData();
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, []); 
  
  if (!user) {
    return <div>Loading...</div>;
  }

  const fetchData = async () => {
    try {
      const [jobsRes, offersRes, marketplaceRes] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/work-offers`),
        axios.get(`${API}/marketplace`)
      ]);
      setJobs(jobsRes.data);
      setWorkOffers(offersRes.data);
      setMarketplaceItems(marketplaceRes.data);

      if (user.user_type === 'employer') {
        const myJobsRes = await axios.get(`${API}/my-jobs`);
        setMyJobs(myJobsRes.data);
      } else {
        const myOffersRes = await axios.get(`${API}/my-offers`);
        setMyOffers(myOffersRes.data);
      }

      const myMarketplaceRes = await axios.get(`${API}/my-marketplace`);
      setMyMarketplaceItems(myMarketplaceRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-green-50">
      {/* Header HeveaTalent */}
      <header className="hevea-header border-b border-green-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center space-x-2 sm:space-x-3 shrink-0">
              <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-white bg-opacity-20 rounded-lg">
                🌳
              </div>
              <div>
                <h1 className="text-base sm:text-xl font-bold text-white leading-tight">HeveaTalent</h1>
                <p className="text-[10px] sm:text-xs text-green-100">{t('heveicultureProfessional')}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-1.5 sm:space-x-4">
              <LanguageSelector />
              <NotificationBell onNavigate={(tab) => setActiveTab(tab)} />
              {user.user_type === 'professional' && (
                <div className="hidden md:block">
                  <ProfessionalAchievements achievements={user?.achievements || []} />
                </div>
              )}
              <Button variant="outline" size="sm" onClick={logout} className="text-xs sm:text-sm px-2 sm:px-4 h-8 sm:h-9">{t('logout')}</Button>
            </div>
          </div>
        </div>
      </header>

      {/* Dashboard Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Current Plan Display */}
        <CurrentPlanBanner onNavigateToPlans={() => setActiveTab('plans')} />

        {/* Onboarding Checklist - Guia de Primeiros Passos */}
        <OnboardingChecklist 
          user={user} 
          onNavigate={(tab) => handleTabChange(tab)} 
        />

        {/* Dashboard Cards removidos para eliminar divergência de dados */}

        {/* Atividades de Hoje */}
        <div className="mb-8 hevea-fade-in">
          <TodaysHabits user={user} />
        </div>

        {/* Ações Rápidas - Mobile Responsive */}
        <div className="bg-white rounded-xl shadow-sm border border-green-100 p-4 md:p-6 mb-8 hevea-fade-in">
          <h2 className="text-lg font-semibold mb-4" style={{color: 'var(--hevea-green)'}}>
            🎯 {t('quickActions')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <button 
              onClick={() => handleTabChange('calendar')}
              data-testid="quick-action-sangria"
              className="hevea-btn-primary p-3 md:p-4 rounded-xl text-center hover:scale-105 transition-transform"
            >
              <div className="text-xl md:text-2xl mb-1 md:mb-2"><img src="/icons/faca_sangria.png" alt="Sangria" className="w-6 h-6 md:w-8 md:h-8 mx-auto" /></div>
              <div className="text-xs md:text-sm font-medium">{t('bleedingToday')}</div>
            </button>
            
            <button 
              onClick={() => handleTabChange('marketplace')}
              data-testid="quick-action-marketplace"
              className="hevea-btn-primary p-3 md:p-4 rounded-xl text-center hover:scale-105 transition-transform"
            >
              <div className="text-xl md:text-2xl mb-1 md:mb-2">➕</div>
              <div className="text-xs md:text-sm font-medium">{t('newAd')}</div>
            </button>
            
            <button 
              onClick={() => handleTabChange('evaluations')}
              data-testid="quick-action-evaluations"
              className="hevea-btn-primary p-3 md:p-4 rounded-xl text-center hover:scale-105 transition-transform"
            >
              <div className="text-xl md:text-2xl mb-1 md:mb-2">🏆</div>
              <div className="text-xs md:text-sm font-medium">{t('evaluations')}</div>
            </button>

            <button 
              onClick={() => window.location.href = `/worker-dashboard/${user.id}`}
              data-testid="quick-action-profile"
              className="hevea-btn-primary p-3 md:p-4 rounded-xl text-center hover:scale-105 transition-transform"
              style={{background: 'linear-gradient(135deg, #1e7a3a, #2d9d4e)'}}
            >
              <div className="text-xl md:text-2xl mb-1 md:mb-2">👤</div>
              <div className="text-xs md:text-sm font-medium">{t('myProfile') || 'Meu Perfil'}</div>
            </button>
          </div>
        </div>

        {/* Navigation Tabs - Mobile First Design */}
        {/* Grid reorganizado: Produção | Carreira | Negócios */}
        <div className="w-full px-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-2">
            {/* Linha 1: PRODUÇÃO (Calendário + Avaliações + Desempenho) */}
            <div className="grid grid-cols-3 gap-2 mb-2">
              <button
                onClick={() => handleTabChange('calendar')}
                className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                  activeTab === 'calendar' 
                    ? 'bg-green-100 text-green-700 border border-green-200' 
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="tab-calendar"
              >
                <div className="text-2xl mb-1">📅</div>
                <div className="text-xs font-semibold">{t('calendar') || 'Calendario'}</div>
              </button>
              
              <button
                onClick={() => handleTabChange('evaluations')}
                className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                  activeTab === 'evaluations' 
                    ? 'bg-green-100 text-green-700 border border-green-200' 
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="tab-evaluations"
              >
                <div className="text-2xl mb-1">🏆</div>
                <div className="text-xs font-semibold">{t('evaluations')}</div>
              </button>
              
              <button
                onClick={() => handleTabChange('performance')}
                className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                  activeTab === 'performance' 
                    ? 'bg-green-100 text-green-700 border border-green-200' 
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="tab-performance"
              >
                <div className="text-2xl mb-1">📈</div>
                <div className="text-xs font-semibold">{t('performance')}</div>
              </button>
            </div>
            
            {/* Linha 2: NEGÓCIOS */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
              {['employer','admin','professional'].includes(user?.user_type) && (
                <button
                  onClick={() => handleTabChange('financeiro')}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                    activeTab === 'financeiro' 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="tab-financeiro"
                >
                  <div className="text-2xl mb-1">💰</div>
                  <div className="text-xs font-semibold">Financeiro</div>
                </button>
              )}
              <button
                onClick={() => handleTabChange('jobs')}
                className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                  activeTab === 'jobs' 
                    ? 'bg-green-100 text-green-700 border border-green-200' 
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div className="text-2xl mb-1">💼</div>
                <div className="text-xs font-semibold">{t('jobs')}</div>
              </button>
              
              <button
                onClick={() => handleTabChange('marketplace')}
                className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                  activeTab === 'marketplace' 
                    ? 'bg-green-100 text-green-700 border border-green-200' 
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div className="text-2xl mb-1">🛒</div>
                <div className="text-xs font-semibold">{t('marketplace')}</div>
              </button>
              
              <button
                onClick={() => handleTabChange('market')}
                className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                  activeTab === 'market' 
                    ? 'bg-green-100 text-green-700 border border-green-200' 
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div className="text-2xl mb-1">📊</div>
                <div className="text-xs font-semibold">{t('marketAnalysisTab')}</div>
              </button>
            </div>
            
            {/* Linha 3: COMUNIDADE, PLANOS & TUTORIAIS */}
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => handleTabChange('community')}
                className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                  activeTab === 'community' 
                    ? 'bg-green-100 text-green-700 border border-green-200' 
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div className="text-2xl mb-1">💬</div>
                <div className="text-xs font-semibold">{t('community')}</div>
              </button>
              
              <button
                data-tab="plans"
                onClick={() => handleTabChange('plans')}
                className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                  activeTab === 'plans' 
                    ? 'bg-green-100 text-green-700 border border-green-200' 
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div className="text-2xl mb-1">💎</div>
                <div className="text-xs font-semibold">{t('plans')}</div>
              </button>

              <button
                onClick={() => window.location.href = '/tutoriais'}
                data-testid="tab-tutoriais"
                className="flex flex-col items-center justify-center p-3 rounded-lg transition-all bg-gray-50 text-gray-600 hover:bg-gray-100"
              >
                <div className="text-2xl mb-1">📺</div>
                <div className="text-xs font-semibold">Tutoriais</div>
              </button>
            </div>
            
            {/* Linha 4: Apenas para Empregadores e Admins */}
            {(user?.user_type === 'employer' || user?.user_type === 'admin') && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  onClick={() => handleTabChange('reports')}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                    activeTab === 'reports' 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <div className="text-2xl mb-1">📋</div>
                  <div className="text-xs font-semibold">{t('reports')}</div>
                </button>
                <button
                  onClick={() => handleTabChange('professionals')}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                    activeTab === 'professionals' 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <div className="text-2xl mb-1">👥</div>
                  <div className="text-xs font-semibold">{t('professionals')}</div>
                </button>
              </div>
            )}

            {/* Linha 5: Mensagens, Estatísticas e Usuários - Apenas para Admins */}
            {user?.user_type === 'admin' && (
              <div className="grid grid-cols-4 gap-2 mt-2">
                <button
                  onClick={() => handleTabChange('messages')}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                    activeTab === 'messages' 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="tab-messages"
                >
                  <div className="text-2xl mb-1">✉️</div>
                  <div className="text-xs font-semibold">{t('msgTitle') || 'Mensagens'}</div>
                </button>
                <button
                  onClick={() => handleTabChange('admin-stats')}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                    activeTab === 'admin-stats' 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="tab-admin-stats"
                >
                  <div className="text-2xl mb-1">📊</div>
                  <div className="text-xs font-semibold">{t('statsTitle') || 'Estatísticas'}</div>
                </button>
                <button
                  onClick={() => handleTabChange('admin-users')}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                    activeTab === 'admin-users' 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="tab-admin-users"
                >
                  <div className="text-2xl mb-1">👥</div>
                  <div className="text-xs font-semibold">{t('usersTitle') || 'Usuários'}</div>
                </button>
                <button
                  onClick={() => handleTabChange('admin-cleanup')}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                    activeTab === 'admin-cleanup'
                      ? 'bg-green-100 text-green-700 border border-green-200'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="tab-admin-cleanup"
                >
                  <div className="text-2xl mb-1">🧹</div>
                  <div className="text-xs font-semibold">Limpeza</div>
                </button>
                <button
                  onClick={() => handleTabChange('admin-radar')}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                    activeTab === 'admin-radar'
                      ? 'bg-green-100 text-green-700 border border-green-200'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="tab-admin-radar"
                >
                  <div className="text-2xl mb-1">🛰️</div>
                  <div className="text-xs font-semibold">Radar</div>
                </button>
                <button
                  onClick={() => handleTabChange('admin-divulgar')}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                    activeTab === 'admin-divulgar'
                      ? 'bg-green-100 text-green-700 border border-green-200'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="tab-admin-divulgar"
                >
                  <div className="text-2xl mb-1">📢</div>
                  <div className="text-xs font-semibold">Divulgar</div>
                </button>
                <button
                  onClick={() => handleTabChange('admin-cursos')}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                    activeTab === 'admin-cursos'
                      ? 'bg-green-100 text-green-700 border border-green-200'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="tab-admin-cursos"
                >
                  <div className="text-2xl mb-1">🎓</div>
                  <div className="text-xs font-semibold">Cursos</div>
                </button>
                <button
                  onClick={() => handleTabChange('admin-feedback')}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                    activeTab === 'admin-feedback'
                      ? 'bg-green-100 text-green-700 border border-green-200'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="tab-admin-feedback"
                >
                  <div className="text-2xl mb-1">💬</div>
                  <div className="text-xs font-semibold">Feedbacks</div>
                </button>
                <button
                  onClick={() => handleTabChange('admin-diagnostico')}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                    activeTab === 'admin-diagnostico'
                      ? 'bg-green-100 text-green-700 border border-green-200'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="tab-admin-diagnostico"
                >
                  <div className="text-2xl mb-1">🔬</div>
                  <div className="text-xs font-semibold">Diagnóstico IA</div>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-4" ref={tabContentRef}>
          <ExperienceFeedbackCard onSeePlans={() => handleTabChange('plans')} />
          {/* Render content only once based on activeTab */}
          {activeTab === 'jobs' && (
            <div key="tab-jobs">
              <PriceWeatherBar />
              {user.user_type === 'employer' ? (
                <EmployerDashboard jobs={jobs} myJobs={myJobs} workOffers={workOffers} onUpdate={fetchData} />
              ) : (
                <ProfessionalDashboard workOffers={workOffers} myOffers={myOffers} onUpdate={fetchData} />
              )}
            </div>
          )}

          {activeTab === 'marketplace' && (
            <div key="tab-marketplace">
              <MarketplaceDashboard 
                items={marketplaceItems} 
                myItems={myMarketplaceItems} 
                onUpdate={fetchData} 
              />
            </div>
          )}

          {activeTab === 'calendar' && (
            <div key="tab-calendar">
              {canAccess('calendar_access') ? (
                <>
                  {isTrial && alertLevel && (
                    <TrialBanner daysRemaining={daysRemaining} alertLevel={alertLevel} planName={planName} onUpgrade={() => handleTabChange('plans')} />
                  )}
                  <BleedingCalendarDashboard />
                </>
              ) : (
                <UpgradeGate feature="calendar" planName={planName} trialExpired={trialExpired} daysRemaining={daysRemaining} isTrial={isTrial} alertLevel={alertLevel} onUpgrade={() => handleTabChange('plans')}>
                  <BleedingCalendarDashboard />
                </UpgradeGate>
              )}
            </div>
          )}

          {activeTab === 'financeiro' && user?.user_type === 'professional' && (
            <div key="tab-financeiro-prof">
              <FinanceiroPanel showEstoque={false} />
            </div>
          )}

          {activeTab === 'financeiro' && ['employer','admin'].includes(user?.user_type) && (
            <div key="tab-financeiro">
              {canAccess('finance_access') ? (
                <>
                  {isTrial && alertLevel && (
                    <TrialBanner daysRemaining={daysRemaining} alertLevel={alertLevel} planName={planName} onUpgrade={() => handleTabChange('plans')} />
                  )}
                  <FinanceiroPanel showEstoque={true} />
                </>
              ) : (
                <UpgradeGate feature="financeiro" planName={planName} trialExpired={trialExpired} daysRemaining={daysRemaining} isTrial={isTrial} alertLevel={alertLevel} onUpgrade={() => handleTabChange('plans')}>
                  <FinanceiroPanel />
                </UpgradeGate>
              )}
            </div>
          )}

          {activeTab === 'community' && (
            <div key="tab-community">
              <PriceWeatherBar />
              <CommunityTipsView />
            </div>
          )}

          {activeTab === 'messages' && user?.user_type === 'admin' && (
            <div key="tab-messages">
              <ContactMessagesPanel />
            </div>
          )}

          {activeTab === 'admin-stats' && user?.user_type === 'admin' && (
            <div key="tab-admin-stats">
              <AdminStatsPanel />
            </div>
          )}

          {activeTab === 'admin-users' && user?.user_type === 'admin' && (
            <div key="tab-admin-users">
              <AdminUsersPanel />
            </div>
          )}

          {activeTab === 'admin-cleanup' && user?.user_type === 'admin' && (
            <div key="tab-admin-cleanup">
              <AdminDataCleanupPanel />
            </div>
          )}

          {activeTab === 'admin-radar' && user?.user_type === 'admin' && (
            <div key="tab-admin-radar">
              <RadarAdminPanel />
            </div>
          )}

          {activeTab === 'admin-divulgar' && user?.user_type === 'admin' && (
            <div key="tab-admin-divulgar">
              <JobBroadcastPanel />
            </div>
          )}

          {activeTab === 'admin-cursos' && user?.user_type === 'admin' && (
            <div key="tab-admin-cursos">
              <CursosAdminPanel />
            </div>
          )}

          {activeTab === 'admin-feedback' && user?.user_type === 'admin' && (
            <div key="tab-admin-feedback">
              <AdminFeedbackPanel />
            </div>
          )}

          {activeTab === 'admin-diagnostico' && user?.user_type === 'admin' && (
            <div key="tab-admin-diagnostico">
              <DiagnosticoIAPanel />
            </div>
          )}

          {activeTab === 'market' && (
            <div key="tab-market">
              <MarketModule />
            </div>
          )}

          {activeTab === 'plans' && (
            <div key="tab-plans">
              <ReferralCard />
              <div id="plans-section">
                <SubscriptionPlansView />
              </div>
              <div className="mt-8">
                <MySubscriptionView onNavigateToPlans={() => {
                  const el = document.getElementById('plans-section');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    el.classList.add('ring-2', 'ring-blue-400', 'rounded-xl');
                    setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400', 'rounded-xl'), 3000);
                  }
                  toast.info('Escolha seu novo plano acima', { duration: 3000 });
                }} />
              </div>
            </div>
          )}

          {(user?.user_type === 'employer' || user?.user_type === 'admin') && activeTab === 'professionals' && (
            <div key="tab-professionals">
              <ProfessionalsSearchView />
            </div>
          )}

          {(user?.user_type === 'employer' || user?.user_type === 'admin') && activeTab === 'reports' && (
            <div key="tab-reports">
              {canAccess('reports_basic') || canAccess('reports_full') || user?.user_type === 'admin' ? (
                <>
                  {isTrial && alertLevel && (
                    <TrialBanner daysRemaining={daysRemaining} alertLevel={alertLevel} planName={planName} onUpgrade={() => handleTabChange('plans')} />
                  )}
                  <ReportsDashboard user={user} />
                </>
              ) : (
                <UpgradeGate feature="reports" planName={planName} trialExpired={trialExpired} daysRemaining={daysRemaining} isTrial={isTrial} alertLevel={alertLevel} onUpgrade={() => handleTabChange('plans')}>
                  <ReportsDashboard user={user} />
                </UpgradeGate>
              )}
            </div>
          )}

          {activeTab === 'evaluations' && (
            <div key="tab-evaluations">
              <EvaluationsDashboard user={user} />
            </div>
          )}

          {activeTab === 'performance' && (
            <div key="tab-performance">
              <ContextualPerformanceDashboard user={user} />
            </div>
          )}
        </div>
        
        {/* Desktop Navigation Tabs - Visual Only */}
        <div className="hidden md:block px-4">
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => handleTabChange('jobs')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'jobs'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
                }`}
              >
                {t('jobCenter')}
              </button>
              <button
                onClick={() => handleTabChange('marketplace')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'marketplace'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
                }`}
              >
                {t('marketplace')}
              </button>
              <button
                onClick={() => handleTabChange('calendar')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'calendar'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
                }`}
              >
                📅 {t('bleedingCalendar')}
              </button>
              <button
                onClick={() => handleTabChange('community')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'community'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
                }`}
              >
                💬 {t('communityTips')}
              </button>
              <button
                data-tab="plans"
                onClick={() => handleTabChange('plans')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'plans'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
                }`}
              >
                💎 {t('plans')}
              </button>
              <button
                onClick={() => handleTabChange('evaluations')}
                data-tab="evaluations"
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'evaluations'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
                }`}
              >
                🏆 {t('evaluations')}
              </button>
              <button
                onClick={() => handleTabChange('performance')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'performance'
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
                }`}
              >
                📊 {t('performance')}
              </button>
              {user?.user_type === 'employer' && (
                <>
                  <button
                    onClick={() => handleTabChange('reports')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'reports'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
                    }`}
                  >
                    📈 {t('reports')}
                  </button>
                  <button
                    onClick={() => handleTabChange('professionals')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'professionals'
                        ? 'border-green-500 text-green-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
                    }`}
                  >
                    👥 {t('professionals')}
                  </button>
                </>
              )}
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

// Employer Dashboard Component - Full functionality restored
// EmployerDashboard foi extraído para /components/EmployerDashboard.jsx

// ProfessionalDashboard foi extraído para /components/ProfessionalDashboard.jsx

// Job Post Form
// JobPostForm foi extraído para /components/JobPostForm.jsx

// WorkOfferForm foi extraído para /components/WorkOfferForm.jsx

// Job Card Component
// JobCard foi extraído para /components/JobCard.jsx

// WorkOfferCard foi extraído para /components/WorkOfferCard.jsx

// Jobs List Component
// JobsList foi extraído para /components/JobsList.jsx

// Current Plan Banner Component
// CurrentPlanBanner foi extraído para /components/CurrentPlanBanner.jsx

// Community Tips View Component
// CommunityTipsView foi extraído para /components/CommunityTipsView.jsx

// Subscription Plans View Component
// SubscriptionPlansView foi extraído para /components/SubscriptionPlansView.jsx

// Professionals Search View Component
// ProfessionalsSearchView foi extraído para /components/ProfessionalsSearchView.jsx

// Evaluations Dashboard Component
// EvaluationsDashboard foi extraído para /components/EvaluationsDashboard.jsx

// App Content Component (inside AuthProvider)
const AppContent = () => {
  const { isAuthenticated, user } = useAuth();
  
  return (
    <div className="App">
      <Toaster position="top-center" richColors closeButton />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MainRoute />} />
          <Route path="/worker-dashboard/:workerId" element={<WorkerDashboard />} />
          <Route path="/evaluation-form" element={<EvaluationForm />} />
          <Route path="/evaluations" element={<EvaluationsPage />} />
          <Route path="/certification" element={<CertificationOptIn />} />
          <Route path="/technical-references" element={<TechnicalReferences />} />
          {/* Página pública para recomendação via convite */}
          <Route path="/recomendar/:inviteCode" element={<PublicRecommendPage />} />
          {/* Módulo de Mercado - Desenvolvimento Isolado (Preview) */}
          <Route path="/mercado-preview" element={<MarketModule />} />
          <Route path="/balcao" element={<BalcaoPublicoPage />} />
          <Route path="/cursos" element={<CursosPublicPage />} />
          <Route path="/parceiros" element={<ParceirosPage />} />
          <Route path="/tutoriais" element={<TutoriaisPage />} />
          <Route path="/vaga/:jobId" element={<PublicJobPage />} />
          <Route path="/vagas" element={<CityIndexPage />} />
          <Route path="/vagas/:slug" element={<CityJobsPage />} />
          <Route path="/opine/:token" element={<FeedbackPublicPage />} />
          <Route path="/confirmar/:token" element={<ConfirmarAnuncioPage />} />
          <Route path="/oferta/:offerId" element={<PublicOfferPage />} />
          <Route path="/curso/:slug" element={<CursoLandingPage />} />
          <Route path="/proposito" element={<PropositoPage />} />
          <Route path="/convite" element={<ConviteTour />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancel" element={<PaymentCancel />} />
          <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
          <Route path="/termos" element={<TermsOfService />} />
          <Route path="/privacidade" element={<PrivacyPolicy />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      {/* CHAT SYSTEM - Isolated Component */}
      {isAuthenticated && user && <ChatSystem user={user} />}
      <InstallPrompt />
      <UpdateNotice />
    </div>
  );
};

// Main App
function App() {
  return (
    <TranslationProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </TranslationProvider>
  );
}

// Componente Modal de Exemplo Reutilizável
// ExampleModal foi extraído para /components/ExampleModal.jsx

// Marketplace Dashboard Component
// MarketplaceDashboard foi extraído para /components/MarketplaceDashboard.jsx

// MarketplaceBrowser foi extraído para /components/MarketplaceBrowser.jsx

// Marketplace Form Component
// MarketplaceForm foi extraído para /components/MarketplaceForm.jsx

// RateSellerButton foi extraído para /components/RateSellerButton.jsx

// Marketplace Item Card Component
// MarketplaceItemCard foi extraído para /components/MarketplaceItemCard.jsx

// Monthly Calendar Visual Component
// MonthlyCalendarView foi extraído para /components/MonthlyCalendarView.jsx

// Simple Charts for Monthly Summary
// MonthlyChartsView foi extraído para /components/MonthlyChartsView.jsx

// Mobile-Friendly Record Form Dialog
// RecordFormDialog foi extraído para /components/RecordFormDialog.jsx

// FeatureConfirmDialog e PixPaymentDialog foram extraídos para /components/MarketplaceDialogs.jsx

// ImageManager foi extraído para /components/ImageManager.jsx

// EditProductDialog foi extraído para /components/EditProductDialog.jsx

// Bleeding Calendar Dashboard
const BleedingCalendarDashboard = () => {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);
  const [trialLoading, setTrialLoading] = useState(true);
  const [showTip, setShowTip] = useState(true);
  const [showGuide, setShowGuide] = useState(false);
  const [showPermissionsGuide, setShowPermissionsGuide] = useState(false);
  const [showTeamListDialog, setShowTeamListDialog] = useState(false);
  const [selectedSangrador, setSelectedSangrador] = useState(null);
  const [teamListData, setTeamListData] = useState(null);
  const [teamListLoading, setTeamListLoading] = useState(false);
  const [workersInfo, setWorkersInfo] = useState(null);
  const { canAccess, planName, isTrial, trialExpired, daysRemaining, alertLevel } = useSubscription();
  const { t } = useTranslation();

  useEffect(() => {
    loadTrialStatus();
    loadProperties();
  }, []);

  // Effect to load workers info when property changes
  useEffect(() => {
    const fetchWorkersInfo = async () => {
      if (!selectedProperty) {
        setWorkersInfo(null);
        return;
      }
      try {
        const response = await axios.get(`${API}/bleeding-property/${selectedProperty.id}/workers-info`);
        setWorkersInfo(response.data);
      } catch (error) {
        console.error("Error loading workers info:", error);
        setWorkersInfo(null);
      }
    };
    fetchWorkersInfo();
  }, [selectedProperty]);

  // Effect to load records when property or month changes
  useEffect(() => {
    const fetchRecords = async () => {
      if (!selectedProperty) return;
      try {
        console.log("Loading records for:", { property: selectedProperty.id, month: currentMonth });
        const response = await axios.get(`${API}/bleeding-records/${selectedProperty.id}?month=${currentMonth}`);
        console.log("Records response received:", response.data.length, "records");
        setRecords(response.data);
      } catch (error) {
        console.error("Error loading records:", error);
        setRecords([]);
      }
    };

    const fetchSummary = async () => {
      if (!selectedProperty) return;
      try {
        console.log("Loading summary for:", { property: selectedProperty.id, month: currentMonth });
        const response = await axios.get(`${API}/bleeding-records/${selectedProperty.id}/summary?month=${currentMonth}`);
        console.log("Summary response received:", response.data);
        setSummary(response.data);
      } catch (error) {
        console.error("Error loading summary:", error);
        setSummary(null);
      }
    };

    if (selectedProperty) {
      // Clear previous data while loading
      setRecords([]);
      setSummary(null);
      
      fetchRecords();
      fetchSummary();
    }
  }, [selectedProperty, currentMonth]);

  const loadTrialStatus = async () => {
    try {
      setTrialLoading(true);
      const response = await axios.get(`${API}/calendar-trial-status`);
      setTrialStatus(response.data);
    } catch (error) {
      console.error("Error loading trial status:", error);
      setTrialStatus({ allowed: true, days_remaining: -1 }); // Fallback
    } finally {
      setTrialLoading(false);
    }
  };

  const loadProperties = async () => {
    try {
      const response = await axios.get(`${API}/bleeding-properties`);
      setProperties(response.data);
      if (response.data.length > 0 && !selectedProperty) {
        setSelectedProperty(response.data[0]);
      }
    } catch (error) {
      console.error("Error loading properties:", error);
    }
  };

  // Function to refresh data after updates (used by child components)
  const refreshData = async () => {
    if (!selectedProperty) return;
    try {
      console.log("Refreshing data for:", { property: selectedProperty.id, month: currentMonth });
      const [recordsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/bleeding-records/${selectedProperty.id}?month=${currentMonth}`),
        axios.get(`${API}/bleeding-records/${selectedProperty.id}/summary?month=${currentMonth}`)
      ]);
      console.log("Refresh complete:", recordsRes.data.length, "records");
      setRecords(recordsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Tooltip Como Usar */}
      {showTip && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 relative">
          <button 
            onClick={() => setShowTip(false)}
            className="absolute top-2 right-2 text-amber-400 hover:text-amber-600"
          >
            ✕
          </button>
          <div className="flex items-start gap-2 pr-6">
            <span className="text-lg">📅</span>
            <div>
              <h4 className="font-semibold text-amber-800">{t('howToUse')}</h4>
              <p className="text-sm text-amber-700 mt-1">{t('tooltipCalendar')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Trial Status Banner */}
      {!trialLoading && trialStatus && (
        <>
          {/* Trial Expired - Block Access */}
          {trialStatus.trial_expired && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-6 text-center">
              <div className="text-4xl mb-4">⏰</div>
              <h3 className="text-xl font-bold text-red-800 mb-2">{t('trialExpiredTitle')}</h3>
              <p className="text-red-700 mb-4">
                {t('trialExpiredMessage')}
                <br />
                <span className="text-sm">{t('trialExpiredDataSaved')}</span>
              </p>
              <div className="flex justify-center gap-4">
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => window.location.href = '#planos'}
                >
                  {t('viewPlansFrom')} R$ {trialStatus.upgrade_price?.toFixed(2)}/{t('month')}
                </Button>
              </div>
            </div>
          )}

          {/* CRITICAL Alert - 1 day remaining */}
          {trialStatus.alert_level === 'critical' && !trialStatus.trial_expired && (
            <div className="bg-red-100 border-2 border-red-500 rounded-lg p-4 flex items-center justify-between animate-pulse">
              <div className="flex items-center">
                <span className="text-3xl mr-3">🚨</span>
                <div>
                  <p className="font-bold text-red-800 text-lg">
                    {t('lastDayOfTrial')}
                  </p>
                  <p className="text-sm text-red-700">
                    {t('lastDayTrialMsg')}
                  </p>
                </div>
              </div>
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => window.location.href = '#planos'}
              >
                {t('upgradeNow')}
              </Button>
            </div>
          )}

          {/* URGENT Alert - 7 days or less */}
          {trialStatus.alert_level === 'urgent' && !trialStatus.trial_expired && (
            <div className="bg-orange-100 border-2 border-orange-500 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-2xl mr-3">⚠️</span>
                <div>
                  <p className="font-bold text-orange-800">
                    {t('trialEndingSoon')} ({trialStatus.days_remaining} {t('daysRemaining')})
                  </p>
                  <p className="text-sm text-orange-700">
                    {t('trialExpiredDataSaved')}
                  </p>
                </div>
              </div>
              <Button 
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={() => window.location.href = '#planos'}
              >
                {t('plans')}
              </Button>
            </div>
          )}

          {/* WARNING Alert - 15 days or less */}
          {trialStatus.alert_level === 'warning' && !trialStatus.trial_expired && (
            <div className="bg-amber-50 border border-amber-400 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-2xl mr-3">⏳</span>
                <div>
                  <p className="font-semibold text-amber-800">
                    {t('trialExpiresIn')} {trialStatus.days_remaining} {t('days')}
                  </p>
                  <p className="text-sm text-amber-700">
                    {t('seeUpgradeOptions')}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="border-amber-500 text-amber-700 hover:bg-amber-100"
                onClick={() => window.location.href = '#planos'}
              >
                {t('plans')}
              </Button>
            </div>
          )}

          {/* INFO Alert - 30 days or less */}
          {trialStatus.alert_level === 'info' && !trialStatus.trial_expired && (
            <div className="bg-blue-50 border border-blue-300 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-xl mr-2">ℹ️</span>
                <p className="text-sm text-blue-700">
                  <strong>{t('trialEnding30Days')}:</strong> {trialStatus.days_remaining} {t('daysRemaining')} 
                  {trialStatus.trial_days_total && ` (de ${trialStatus.trial_days_total} ${t('days')})`}
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                className="text-blue-600 hover:bg-blue-100"
                onClick={() => window.location.href = '#planos'}
              >
                {t('plans')}
              </Button>
            </div>
          )}

          {/* No alert - More than 30 days remaining */}
          {!trialStatus.alert_level && trialStatus.days_remaining > 0 && trialStatus.days_remaining !== -1 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center">
              <span className="text-xl mr-2">✅</span>
              <p className="text-sm text-green-700">
                <strong>{t('trialEnding30Days')}:</strong> {t('trialEnding30DaysMsg')} {trialStatus.days_remaining} {t('daysToExplore')}
              </p>
            </div>
          )}

          {/* Professional without Plus - Show trial offer */}
          {trialStatus.show_trial_offer && (
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6 text-center">
              <div className="text-4xl mb-3">📅</div>
              <h3 className="text-lg font-bold text-purple-800 mb-2">{t('bleedingCalendar')}</h3>
              <p className="text-purple-700 mb-4">
                {t('calendarSubtitle')}
                <br />
                <strong>{t('startFreeTrial')}</strong>
              </p>
              <Button 
                className="bg-purple-600 hover:bg-purple-700"
                onClick={() => window.location.href = '#planos'}
              >
                {t('startFreeTrial')}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Block content if trial expired */}
      {trialStatus?.trial_expired ? (
        <div className="opacity-50 pointer-events-none">
          <div className="text-center py-12 text-gray-500">
            <Calendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>{t('contentBlocked')}</p>
            <p className="text-sm mt-2">{t('trialExpiredDataSaved')}</p>
          </div>
        </div>
      ) : (
        <>
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h2 className="text-xl sm:text-2xl font-bold flex items-center">
          <Calendar className="w-6 h-6 mr-2" />
          {t('bleedingCalendar')}
        </h2>
        <Button onClick={() => setShowPropertyForm(true)} disabled={trialStatus?.trial_expired || trialStatus?.show_trial_offer}>
          <Plus className="w-4 h-4 mr-2" />
          {t('newProperty')}
        </Button>
      </div>

      {/* Property Selection */}
      <Card>
        <CardHeader>
          <CardTitle>{t('selectProperty')}</CardTitle>
        </CardHeader>
        <CardContent>
          {properties.length === 0 ? (
            <div className="text-center py-8 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="text-4xl mb-4">🌱</div>
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">{t('addFirstProperty')}</h3>
              <p className="text-yellow-700 mb-4">{t('noPropertyMsg')}</p>
              <Button onClick={() => setShowPropertyForm(true)} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                {t('addPropertyBtn')}
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>{t('property')}</Label>
                <select 
                  value={selectedProperty?.id || ""} 
                  onChange={(e) => {
                    const property = properties.find(p => p.id === e.target.value);
                    setSelectedProperty(property);
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">{t('selectProperty')}</option>
                  {properties.map(property => (
                    <option key={property.id} value={property.id}>
                      {property.name} - {property.culture_age_years} {t('years')}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>{t('monthYear')}</Label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const [year, month] = currentMonth.split('-').map(Number);
                      const newDate = new Date(year, month - 2, 1);
                      setCurrentMonth(newDate.toISOString().slice(0, 7));
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg border"
                    title={t('previousMonth')}
                  >
                    ◀
                  </button>
                  <Input 
                    type="month" 
                    value={currentMonth}
                    onChange={(e) => setCurrentMonth(e.target.value)}
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const [year, month] = currentMonth.split('-').map(Number);
                      const newDate = new Date(year, month, 1);
                      setCurrentMonth(newDate.toISOString().slice(0, 7));
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg border"
                    title={t('nextMonth')}
                  >
                    ▶
                  </button>
                </div>
              </div>
              <div>
                <Label>{t('location')}</Label>
                <p className="text-sm text-gray-600 mt-2">{selectedProperty?.location || t('selectProperty')}</p>
              </div>
              {/* Botões de Guia - Linha separada */}
              <div className="md:col-span-3 flex flex-wrap gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowGuide(true)}
                  className="px-3 py-1.5 hover:bg-green-100 rounded-lg border border-green-300 bg-green-50 text-green-700 flex items-center gap-1 text-sm"
                  title={t('calendarGuideTitle')}
                  data-testid="calendar-guide-btn"
                >
                  📖 {t('calendarGuideTitle')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPermissionsGuide(true)}
                  className="px-3 py-1.5 hover:bg-indigo-100 rounded-lg border border-indigo-300 bg-indigo-50 text-indigo-700 flex items-center gap-1 text-sm"
                  title="Permissões"
                  data-testid="permissions-guide-btn"
                >
                  🔐 {t('permGuideTitle')}
                </button>
                {selectedProperty && (
                  <button
                    type="button"
                    onClick={() => {
                      const backendUrl = process.env.REACT_APP_BACKEND_URL;
                      window.open(`${backendUrl}/api/admin/safra-configs-view`, '_blank');
                    }}
                    className="px-3 py-1.5 hover:bg-amber-100 rounded-lg border border-amber-300 bg-amber-50 text-amber-700 flex items-center gap-1 text-sm"
                    title={t('configSafraTooltip') || 'Configurar plantas por sangrador, modo de sangria e turnover de equipe por safra'}
                    data-testid="config-safra-btn"
                  >
                    🗓️ {t('configSafraBtn') || 'Configurar Safra'}
                  </button>
                )}
                {selectedProperty && (
                  <button
                    type="button"
                    onClick={async () => {
                      setShowTeamListDialog(true);
                      setTeamListLoading(true);
                      setTeamListData(null);
                      try {
                        const r = await axios.get(
                          `${API}/bleeding-records/${selectedProperty.id}/team-report-v2`,
                          { params: { month: currentMonth } }
                        );
                        setTeamListData(r.data);
                      } catch (e) {
                        setTeamListData({ error: e.response?.data?.detail || 'Erro ao carregar equipe' });
                      } finally {
                        setTeamListLoading(false);
                      }
                    }}
                    className="px-3 py-1.5 hover:bg-purple-100 rounded-lg border border-purple-300 bg-purple-50 text-purple-700 flex items-center gap-1 text-sm"
                    title="Ver dashboard consolidado de cada sangrador da equipe"
                    data-testid="team-dashboard-btn"
                  >
                    👥 {t('teamButton') || 'Equipe'}
                  </button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Monthly Summary */}
      {summary && (
        <Card className="hevea-card mb-6">
          <CardHeader>
            <CardTitle style={{color: 'var(--hevea-green)'}}>📊 {t('monthlySummary')}</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Métricas do mês */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div key={`chuva-${summary.summary.total_chuva_mm}`} className="hevea-metric-card">
                <div className="text-2xl font-bold" style={{color: 'var(--hevea-rain)'}}>{summary.summary.total_chuva_mm}mm</div>
                <div className="text-sm text-gray-600">🌧️ {t('totalRain')}</div>
              </div>
              <div key={`producao-${summary.summary.total_producao_kg}`} className="hevea-metric-card">
                <div className="text-2xl font-bold" style={{color: 'var(--hevea-production)'}}>{summary.summary.total_producao_kg}kg</div>
                <div className="text-sm text-gray-600">📦 {t('totalProduction')}</div>
              </div>
              <div key={`sangria-${summary.summary.total_sangria_dias}`} className="hevea-metric-card">
                <div className="text-2xl font-bold" style={{color: 'var(--hevea-green)'}}>{summary.summary.total_sangria_dias}</div>
                <div className="text-sm text-gray-600 flex items-center justify-center gap-1"><img src="/icons/faca_sangria.png" alt="" className="w-4 h-4" /> {t('bleedingDays')}</div>
              </div>
              <div key={`reposicao-${summary.summary.total_reposicao}`} className="hevea-metric-card">
                <div className="text-2xl font-bold" style={{color: 'var(--hevea-orange)'}}>{summary.summary.total_reposicao}</div>
                <div className="text-sm text-gray-600">🔄 {t('totalReplacements')}</div>
              </div>
              {/* Indicador de Trabalhadores */}
              {workersInfo && workersInfo.team_management_enabled && (
                <div className={`hevea-metric-card ${workersInfo.workers.at_limit ? 'border-2 border-red-400' : ''}`}>
                  <div className={`text-2xl font-bold ${workersInfo.workers.at_limit ? 'text-red-600' : 'text-indigo-600'}`}>
                    {workersInfo.workers.current}
                  </div>
                  <div className="text-sm text-gray-600">👷 {t('tappers')}</div>
                  <div className={`text-xs mt-1 ${workersInfo.workers.at_limit ? 'text-red-500' : 'text-green-600'}`}>
                    {workersInfo.workers.is_unlimited 
                      ? t('unlimited')
                      : `${t('of')} ${workersInfo.workers.limit}`
                    }
                    {workersInfo.workers.at_limit && ` (${t('limitReached')})`}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Yearly Accumulated Summary */}
      {selectedProperty && (
        <div className="mb-6">
          <YearlySummaryCard 
            propertyId={selectedProperty.id} 
            currentMonth={currentMonth}
          />
        </div>
      )}

      {/* Ethrel Alert Banner */}
      {selectedProperty && (
        <EthrelAlertBanner 
          propertyId={selectedProperty.id}
          onRefresh={refreshData}
        />
      )}

      {/* Monthly Report Section */}
      {selectedProperty && (
        <MonthlyReportView 
          property={selectedProperty} 
          month={currentMonth}
        />
      )}

      {/* Workers Database Section */}
      {selectedProperty && canAccess('team_management') && (
        <WorkersDatabase 
          property={selectedProperty} 
          month={currentMonth}
        />
      )}
      {selectedProperty && !canAccess('team_management') && (
        <UpgradeGate feature="team" planName={planName} trialExpired={trialExpired} daysRemaining={daysRemaining} isTrial={isTrial} alertLevel={alertLevel} onUpgrade={() => window.dispatchEvent(new CustomEvent('changeTab', { detail: 'plans' }))}>
          <div className="h-40 bg-gray-50 rounded-xl flex items-center justify-center text-gray-400">
            <span>{t('gateTeam')}</span>
          </div>
        </UpgradeGate>
      )}

      {/* Team Report Section - NEW FASE 2 */}
      {selectedProperty && canAccess('team_management') && (
        <TeamReportView 
          property={selectedProperty} 
          month={currentMonth}
        />
      )}

      {selectedProperty && (
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-blue-800 mb-2">📋 {t('appHowToUse')}</h3>
            <div className="grid md:grid-cols-4 gap-2 text-sm text-blue-700 mb-3">
              <div className="flex items-center">
                <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold mr-2">1</span>
                {t('appPropertySelected')} ✓
              </div>
              <div className="flex items-center">
                <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold mr-2">2</span>
                {t('appChooseMonth')} ✓
              </div>
              <div className="flex items-center">
                <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-xs font-bold mr-2">3</span>
                <strong>{t('appClickDayToAdd')}</strong>
              </div>
              <div className="flex items-center">
                <span className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center text-xs font-bold mr-2">4</span>
                {t('appUseTableToEdit')}
              </div>
            </div>
            {/* Legenda */}
            <div className="flex flex-wrap items-center gap-3 text-xs text-blue-600 border-t border-blue-200 pt-3">
              <span className="font-medium">{t('appLegend')}</span>
              <span className="flex items-center gap-1 bg-green-100 px-2 py-1 rounded"><img src="/icons/faca_sangria.png" alt="" className="w-4 h-4" /> {t('appBleeding')}</span>
              <span className="flex items-center gap-1 bg-blue-100 px-2 py-1 rounded">🔄 {t('appReplacement')}</span>
              <span className="flex items-center gap-1 bg-purple-100 px-2 py-1 rounded">🧪 {t('appFungicide')}</span>
              <span className="flex items-center gap-1 bg-orange-100 px-2 py-1 rounded">⚡ Ethrel</span>
              <span className="flex items-center gap-1 bg-sky-100 px-2 py-1 rounded">🌧️ {t('appRain')}</span>
              <span className="flex items-center gap-1 bg-amber-100 px-2 py-1 rounded">📦 {t('appProduction')}</span>
            </div>
            {/* Alerta de dias sem registro */}
            {(() => {
              const today = new Date();
              const [year, month] = currentMonth.split('-').map(Number);
              const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month;
              if (isCurrentMonth && summary?.summary) {
                const dayOfMonth = today.getDate();
                const daysWithRecords = summary.summary.total_records || 0;
                const daysWithoutRecord = dayOfMonth - daysWithRecords;
                if (daysWithoutRecord >= 3) {
                  return (
                    <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-xs flex items-center gap-2">
                      <span>⚠️</span>
                      <span>{t('appYouHave')} <strong>{daysWithoutRecord} {t('appDaysWithoutRecord')}</strong></span>
                    </div>
                  );
                }
              }
              return null;
            })()}
          </CardContent>
        </Card>
      )}

      {/* Daily Records Table */}
      {selectedProperty && (
        <BleedingDailyTable 
          key={`${selectedProperty.id}-${currentMonth}`}
          property={selectedProperty} 
          month={currentMonth} 
          records={records} 
          onUpdate={refreshData} 
        />
      )}

      {/* Property Form Dialog */}
      {showPropertyForm && <PropertyFormDialog onClose={() => setShowPropertyForm(false)} onSave={loadProperties} />}
      
      {/* Calendar Guide Modal */}
      <CalendarGuideModal isOpen={showGuide} onClose={() => setShowGuide(false)} />
      
      {/* Permissions Guide Modal - Unified with Management */}
      <PropertyPermissionsGuide 
        isOpen={showPermissionsGuide} 
        onClose={() => setShowPermissionsGuide(false)}
        property={selectedProperty}
        onUpdate={loadProperties}
      />

      {/* Team List Dialog: lista clicável de sangradores → abre SangradorDashboard */}
      <Dialog open={showTeamListDialog} onOpenChange={setShowTeamListDialog}>
        <DialogContent className="max-w-md" data-testid="team-list-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              👥 Equipe — {selectedProperty?.name}
            </DialogTitle>
            <DialogDescription>
              {currentMonth} • clique em um sangrador para ver o dashboard
            </DialogDescription>
          </DialogHeader>
          {teamListLoading && <div className="text-center py-6 text-gray-500">Carregando equipe...</div>}
          {teamListData?.error && <div className="text-red-700 bg-red-50 p-3 rounded">⚠️ {teamListData.error}</div>}
          {teamListData && !teamListData.error && (
            <div className="space-y-2">
              {(() => {
                // Combinar sangradores ativos + inativos (paralisados) numa única lista.
                // Filtra a entrada agregada "Equipe (sem responsável)" do team-report-v2,
                // que não representa um sangrador real, apenas a soma de registros sem nome.
                const SEM_RESP_LABELS = ['equipe (sem responsável)', 'equipe (sem responsavel)'];
                const isRealWorker = (nome) => {
                  const n = (nome || '').trim().toLowerCase();
                  return n && !SEM_RESP_LABELS.includes(n);
                };
                const ativos = (teamListData.sangradores || [])
                  .filter(s => isRealWorker(s.nome))
                  .map(s => ({...s, status: 'ativo'}));
                const inativos = (teamListData.sangradores_inativos_no_mes || [])
                  .filter(s => isRealWorker(s.nome))
                  .map(s => ({
                    nome: s.nome, plantas_individuais: s.plantas, status: 'inativo'
                  }));
                const todos = [...ativos, ...inativos];
                if (todos.length === 0) {
                  return (
                    <p className="text-amber-700 bg-amber-50 p-3 rounded text-sm" data-testid="team-list-empty">
                      ⚠️ Nenhum sangrador encontrado neste mês. Configure a alocação em <strong>Configurar Safra</strong>.
                    </p>
                  );
                }
                return todos.map((s, i) => {
                  const plantas = s.plantas_individuais;
                  const saiu = plantas === 0;
                  return (
                    <button
                      key={`${s.nome}-${i}`}
                      type="button"
                      onClick={() => {
                        setSelectedSangrador(s.nome);
                        setShowTeamListDialog(false);
                      }}
                      className={`w-full text-left p-3 rounded-lg border hover:shadow transition ${
                        saiu
                          ? 'bg-gray-50 border-gray-300 hover:border-gray-400 opacity-75'
                          : s.status === 'ativo'
                            ? 'bg-emerald-50 border-emerald-200 hover:border-emerald-400'
                            : 'bg-amber-50 border-amber-200 hover:border-amber-400'
                      }`}
                      data-testid={`team-list-worker-${i}`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <span className={`font-semibold ${saiu ? 'line-through text-gray-600' : ''}`}>
                            {saiu ? '🚪 ' : ''}{s.nome}
                          </span>
                          {saiu && <span className="ml-2 text-xs text-gray-600">(saiu — plantas transferidas)</span>}
                          {!saiu && s.status === 'inativo' && <span className="ml-2 text-xs text-amber-700">(sem registros no mês)</span>}
                        </div>
                        <div className="text-xs text-gray-600">
                          {plantas != null && plantas > 0 ? `${plantas.toLocaleString('pt-BR')} plantas` : ''}
                        </div>
                      </div>
                    </button>
                  );
                });
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dashboard individual do sangrador */}
      {selectedSangrador && selectedProperty && (
        <SangradorDashboard
          open={!!selectedSangrador}
          onClose={() => setSelectedSangrador(null)}
          sangradorNome={selectedSangrador}
          propertyId={selectedProperty.id}
          month={currentMonth}
          propertyName={selectedProperty.name}
        />
      )}
        </>
      )}
    </div>
  );
};

// Bleeding Daily Table Component - Mobile Optimized
// BleedingDailyTable foi extraído para /components/BleedingDailyTable.jsx

// Workers Database Component - Internal consultation system
// WorkersDatabase foi extraído para /components/WorkersDatabase.jsx

// Monthly Report Component
// MonthlyReportView foi extraído para /components/MonthlyReportView.jsx

// PropertyFormDialog foi extraído para /components/PropertyFormDialog.jsx

// ====================================================================
// TEAM REPORT COMPONENT - FASE 2 (MINIMALISTA)
// TeamReportView foi extraído para /components/TeamReportView.jsx
// ====================================================================



// ====================================================================
// CHAT SYSTEM COMPONENT - ISOLATED IMPLEMENTATION (ULTRA CAUTIOUS)
// ChatSystem foi extraído para /components/ChatSystem.jsx
// ====================================================================

const MainRoute = () => {
  const { isAuthenticated } = useAuth();
  return (
    <PortalErrorBoundary>
      {isAuthenticated ? <Dashboard /> : <LandingPage />}
    </PortalErrorBoundary>
  );
};

export default App;